import { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { addRpcUrlOverrideToChain, useFundWallet, useLinkAccount, usePrivy } from '@privy-io/react-auth';
import { ronin, saigon } from 'viem/chains';

import useChest from '../hook/useChest';
import useDailyCheckIn from '../hook/useDailyCheckin';
import useFish from '../hook/useFish';
import useCustomLogout from '../hook/useLogout';
import usePurchase from '../hook/usePurchase';
import useRod from '../hook/useRod';
import useTicket from '../hook/useTicket';
import useTwitter from '../hook/useTwitter';
import useWithdraw from '../hook/useWithdraw';
import useNFTChanges from '../hook/useNFTChanges';
import useLinkWallet from '../hook/useLinkWallet';
import useToken from '../hook/useToken';
import gameImage from '../bg-game.png';
import { disableNotifications, enableGameNotifications } from '../serviceWorkerRegistration';
import { isTelegram, os } from '../utils/browser';
import environments from '../utils/environments';
import useSpinWheel from '../hook/useSpinWheel';

const { PUBLIC_URL, PUBLIC_RPC_URL, GAME_VERSION, FISH_BE_ENDPOINT, FISH_WEBSOCKET_ENDPOINT } = environments;

let baseCustomRPC;

const isProductionEVN = process.env.REACT_APP_ENVIRONMENT === 'prod';
const isShowBtn = process.env.REACT_APP_SHOW_DEV_MODE_BTN === 'true';

if (isProductionEVN) {
  baseCustomRPC = addRpcUrlOverrideToChain(ronin, PUBLIC_RPC_URL);
} else {
  baseCustomRPC = addRpcUrlOverrideToChain(saigon, PUBLIC_RPC_URL);
}

const telegramBot = process.env.REACT_APP_TELEGRAM_BOT_NAME;
const telegramApp = process.env.REACT_APP_TELEGRAM_APP_NAME;
const videoURL = process.env.REACT_APP_INTRO_VIDEO_URL;

const GameComponent = () => {
  const [tokenIds, setTokenIds] = useState([]); // State for list of tokenIds
  const [showVideo, setShowVideo] = useState(true);
  const [showPlayBtn, setShowPlayBtn] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isGameLoaded, setIsGameLoaded] = useState(false);
  const [devMode, setDevMode] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('');
  const { user, unlinkTwitter, exportWallet } = usePrivy();
  const { postTweet, updateTwitterInfo } = useTwitter();
  const { customLogout } = useCustomLogout();
  const { purchaseGold, purchasePack, purchaseDivingTicket } = usePurchase();
  const { purchaseTournamentTicket } = useTicket();
  const { handleWithdraw } = useWithdraw();
  const { stakeFishToken, swapRonToFish, getSwapPreview } = useToken();
  const { openLuckyChest, withdrawChest, openBatchNFTChest } = useChest();
  const { depositRod, withdrawRod, repairRod } = useRod();
  const { dailyCheckIn } = useDailyCheckIn();
  const { mintFish, withdrawFish, depositFish, cooking, addToCollection } = useFish();
  const { spin } = useSpinWheel();
  const { linkTwitter } = useLinkAccount({
    onSuccess: async (linkedAccount) => {
      if (linkedAccount?.twitter) {
        await updateTwitterInfo(linkedAccount.twitter);
      } else {
        const twitter = linkedAccount.user.twitter;
        twitter.avatar = twitter.profilePictureUrl;
        await updateTwitterInfo(twitter);
      }
    },
  });
  const { getWalletAvailability, linkWallet } = useLinkWallet();

  const { fundWallet } = useFundWallet({
    onUserExited() {
      sendMsgToGameClient({
        type: 'depositWalletResult',
        // message: 'Deposit Successful',
        // isSuccess: true,
        hasWaiting: true,
      });
      activeWaiting(false);
    },
  });

  const handleTokenIdsChange = (event) => {
    const input = event.target.value;
    // Split the input by commas, trim whitespace, and parse as integers
    const ids = input
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    setTokenIds(ids);
  };

  const toggleDevMode = () => {
    setDevMode((prevDevMode) => !prevDevMode);
  };

  const twitterSubject = user?.twitter?.subject || null;

  const [containerStyle, setContainerStyle] = useState({
    height: '100dvh',
    width: '100vw',
    margin: 'auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  });

  const iframeRef = useRef(null);

  useEffect(() => {
    return () => {
      localStorage.removeItem('reauth_done');
    };
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.source === iframeRef.current?.contentWindow) {
        try {
          if (event.data.type === 'buttonLogOutClicked') {
            cocosLogoutBtnClick(event.data.data);
          } else if (event.data.type === 'buttonBuyCoinClicked') {
            cocosExchangeGoldBtnClick(event.data.data);
          } else if (event.data.type === 'buttonPurchaseStarterPackClicked') {
            onInGameBuyPackageBtnClicked(
              event.data.amountETH,
              event.data.packId,
              event.data.packName,
              event.data.paymentToken
            );
          } else if (event.data.type === 'buttonBuyDivingTicketClicked') {
            onInGameBuyDivingTicketBtnClicked(event.data.mode, event.data.paymentToken);
          } else if (event.data.type === 'buttonBuyEventTicketClicked') {
            cocosBuyEventTicketBtnClick(event.data.amountETH);
          } else if (event.data.type === 'buttonDepositClicked') {
            cocosDepositBtnClick(event.data.message);
          } else if (event.data.type === 'buttonExportWalletClicked') {
            cocosExportWalletBtnClick(event.data.data);
          } else if (event.data.type === 'buttonWithdrawClicked') {
            cocosWithdrawBtnClick(event.data.address, event.data.amountETH);
          } else if (event.data.type === 'buttonLinkTwitterClicked') {
            cocosLinkTwitterBtnClick(event.data.data);
          } else if (event.data.type === 'buttonUnlinkTwitterClicked') {
            cocosUnlinkTwitterBtnClick(event.data.data);
          } else if (event.data.type === 'buttonShareFishClicked') {
            cocosShareFishBtnClick(event.data.data);
          } else if (event.data.type === 'buttonShareOnTelegram') {
            cocosShareTelegramBtnClick(event.data.data);
          } else if (event.data.type === 'OpenTelegramInvoice') {
            cocosOpenTelegramInvoice(event.data.data);
          } else if (event.data.type === 'OpenHttpLink') {
            cocosOpenLink(event.data.data);
          } else if (event.data.type === 'buttonStakeFISHClicked') {
            onInGameStakeBtnClicked(event.data.amount, event.data.durationInSeconds);
          } else if (event.data.type === 'swapInputChanged') {
            onInGameSwapInputChanged(event.data.inputToken, event.data.amount);
          } else if (event.data.type === 'buttonSwapClicked') {
            onInGameSwapBtnClicked(event.data.amount);
          } else if (event.data.type === 'buttonSpinDailyTokenWheelClicked') {
            onInGameSpinTokenWheelClicked();
          } else if (event.data.type === 'buttonOpenLuckyChestClicked') {
            onInGameOpenLuckyChestBtnClicked(event.data.tokenId);
          } else if (event.data.type === 'buttonWithdrawNFTChestClicked') {
            cocosWithdrawNFTChestBtnClick(event.data.chestInfos);
          } else if (event.data.type === 'buttonOpenBatchNFTChestClicked') {
            onInGameOpenBatchNFTChestCliked(event.data.chestInfos);
          } else if (event.data.type === 'buttonDepositNFTRodClicked') {
            cocosDepositRodBtnClick(event.data.tokenIds);
          } else if (event.data.type === 'buttonWithdrawNFTRodClicked') {
            cocosWithdrawRodBtnClick(event.data.tokenIds);
          } else if (event.data.type === 'buttonRepairNFTRodClicked') {
            cocosRepairRodBtnClick(event.data.tokenId, event.data.paymentToken);
          } else if (event.data.type === 'buttonDailyCheckInClicked') {
            cocosDailyCheckInBtnClick(event.data.questId);
          } else if (event.data.type === 'buttonMintNFTFishClicked') {
            cocosMintFishBtnClick(event.data.fishIds);
          } else if (event.data.type === 'buttonWithdrawNFTFishClicked') {
            cocosWithdrawFishBtnClick(event.data.fishIds, event.data.tokenIds);
          } else if (event.data.type === 'buttonDepositNFTFishClicked') {
            cocosDepositFishBtnClick(event.data.tokenIds);
          } else if (event.data.type === 'buttonBurnNFTFishClicked') {
            cocosCookingBtnClick(event.data.inGameTokenIds, event.data.inWalletTokenIds);
          } else if (event.data.type === 'buttonCollectNFTFishClicked') {
            onInGameCollectNFTFishBtnClicked(event.data.fish);
          } else if (event.data.type === 'buttonLinkWalletClicked') {
            onInGameLinkWalletBtnClicked();
          } else if (event.data.type === 'walletProviderSelected') {
            onWalletProviderSelected(event.data.providerKey);
          } else if (event.data.type === 'buttonTurnOffIntro') {
            cocosTurnOffIntro();
          } else if (event.data.type === 'buttonTurnOnOffNotification') {
            handleNotificationSettingsClick(event.data);
          } else if (event.data.type === 'actionNotificationBrowser') {
            changeNotificationPwa(event.data);
          } else if (event.data.type === 'loadingProgress') {
            cocosLoadingProgress(event.data);
          } else {
            const authToken = localStorage.getItem('fishAuth');
            const refreshToken = localStorage.getItem('refreshToken');
            const userId = localStorage.getItem('userId');

            const authData = {
              authToken,
              refreshToken,
              userId,
              beAddress: FISH_BE_ENDPOINT,
              websocketAddress: FISH_WEBSOCKET_ENDPOINT,
              isTelegram,
              isMobile,
              os,
              telegramBot,
              telegramApp,
              gameVersion: GAME_VERSION,
              gameUrl: PUBLIC_URL,
              type: 'initAuth',
            };

            // 1️⃣ Send to the Cocos game iframe
            if (iframeRef?.current?.contentWindow) {
              iframeRef.current.contentWindow.postMessage(authData, '*');
            }

            // 2️⃣ Send to parent (if in iframe)
            if (window.parent !== window) {
              window.parent.postMessage(authData, '*');
            }
          }
        } catch (error) {
          console.error('Error handling message:', error);
        }
      } else if (event.data.type === 'actionNotificationBrowser') {
        changeNotificationPwa(event.data);
      }
    };

    window.addEventListener('message', handleMessage);

    const iframe = iframeRef.current;
    if (iframe) {
      window.dispatchGameMessageEvent = (data) => iframeRef.current.contentWindow.postMessage(data, '*');
      setIsGameLoaded(true);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useNFTChanges(isGameLoaded && !showVideo);

  const activeWaiting = async (isWaiting) => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage({ type: 'activeWaiting', isWaiting: isWaiting }, '*');
    }
  };

  const changeNotificationPwa = async (data) => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage(data, '*');
    }
  };

  const sendMsgToGameClient = async ({ type, message, success, hasWaiting, data, ...rest }) => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage(
        {
          type,
          message,
          isSuccess: success,
          hasWaiting,
          data,
          ...rest, // custom data
        },
        '*'
      );
    }
  };

  const cocosLogoutBtnClick = async (data) => {
    customLogout();
  };

  const cocosExchangeGoldBtnClick = async (input) => {
    activeWaiting(true);
    const { success, message, data } = await purchaseGold(input);
    activeWaiting(false);
    // sendMsgToGameClient({ type: 'refreshUserData', message, success, hasWaiting: true });
    sendMsgToGameClient({ type: 'purchaseGoldResult', message, success, hasWaiting: true, data });
  };

  const onInGameBuyPackageBtnClicked = async (amountETH, packId, packName, token) => {
    activeWaiting(true);
    const { success, message, data } = await purchasePack(amountETH, packId, packName, token);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'purchasePackResult', message, success, hasWaiting: true, data });
  };

  const onInGameBuyDivingTicketBtnClicked = async (mode, token) => {
    activeWaiting(true);
    const { success, message, data } = await purchaseDivingTicket(mode, token);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'purchaseDivingTicketResult', message, success, hasWaiting: true, data });
  };

  const cocosBuyEventTicketBtnClick = async (amountETH) => {
    activeWaiting(true);
    const { success, message } = await purchaseTournamentTicket(amountETH, 1);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'buyTicketResult', message, success, hasWaiting: true });
  };

  const cocosTurnOffIntro = async () => {
    setShowVideo(false);
  };

  const cocosDepositBtnClick = async (amount) => {
    const embeddedWallet = user.linkedAccounts.find((acc) => acc.connectorType === 'embedded');
    await fundWallet(embeddedWallet?.address || user.wallet.address, {
      chain: baseCustomRPC,
      amount: amount.toString(),
    });
  };

  const onInGameStakeBtnClicked = async (amount, duration) => {
    activeWaiting(true);
    const { success, message, data } = await stakeFishToken(amount, duration);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'stakeFISHResult', message, success, hasWaiting: true, data });
  };

  const onInGameSwapInputChanged = async (inputToken, amount) => {
    activeWaiting(true);
    const { success, message, data } = await getSwapPreview({ inputToken, amount });
    activeWaiting(false);
    sendMsgToGameClient({ type: 'swapQuoteData', message, success, hasWaiting: true, data });
  };

  const onInGameSwapBtnClicked = async (ronAmount) => {
    activeWaiting(true);
    const { success, message, data } = await swapRonToFish(ronAmount);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'swapResult', message, success, hasWaiting: true, data });
  };

  const onInGameSpinTokenWheelClicked = async () => {
    activeWaiting(true);
    const { success, message, data } = await spin();
    activeWaiting(false);
    sendMsgToGameClient({ type: 'spinTokenWheelResult', message, success, hasWaiting: true, data });
  };

  const cocosExportWalletBtnClick = (data) => {
    exportWallet();
  };

  const cocosLinkTwitterBtnClick = async (data) => {
    if (!user.twitter?.subject) {
      linkTwitter();
    } else {
      console.warn('Twitter account has already linked!');
    }
  };

  const cocosUnlinkTwitterBtnClick = async (data) => {
    try {
      await unlinkTwitter(twitterSubject);
      await updateTwitterInfo(null);
      sendMsgToGameClient({
        type: 'refreshUserUnlink',
        message: 'Successfully unlink X',
        hasWaiting: true,
        isSuccess: true,
      });
    } catch (error) {
      console.error('Unlink Twitter failed:', error);
    }
  };

  const cocosWithdrawBtnClick = async (address, amount) => {
    const tx = await handleWithdraw(address.trim(), amount.trim());
    if (tx) {
      sendMsgToGameClient({
        type: 'withdrawWalletResult',
        // message: 'Withdraw Successful',
        // isSuccess: true,
        hasWaiting: true,
      });
    }
  };

  const cocosShareFishBtnClick = async (data) => {
    await postTweet(data);
  };

  const cocosShareTelegramBtnClick = async (refCode) => {
    const message = 'FISHING FRENZY' + '\n';
    const link = 'https://t.me/share/url?url=https://t.me/' + telegramBot + '/' + telegramApp + '?startapp=' + refCode; // + "&text=" + encodeURIComponent(message);

    // window.Telegram.WebApp.openLink(link);
    window.Telegram.WebApp.openTelegramLink(link);
  };

  const cocosOpenLink = async (link) => {
    try {
      if (isTelegram) window.Telegram.WebApp.openLink(link);
      else window.open(link);
    } catch (error) {
      console.error('Open link error:', error);
    }
    activeWaiting(false);
  };

  const cocosOpenTelegramInvoice = async (invoiceLink) => {
    window.Telegram.WebApp.openLink(invoiceLink);
  };

  const onInGameOpenLuckyChestBtnClicked = async (tokenId) => {
    const { success, message, data } = await openLuckyChest(tokenId, () => {
      sendMsgToGameClient({ type: 'getConfirmOpenNFTChest', message: 'Confirm Successful' });
    });
    sendMsgToGameClient({ type: 'openNFTChestResult', message, success, hasWaiting: true, data });
  };

  const cocosWithdrawNFTChestBtnClick = async (chestInfos) => {
    const { success, message, data } = await withdrawChest(chestInfos);
    sendMsgToGameClient({ type: 'withdrawChestResult', message, success, hasWaiting: true, data });
  };

  const onInGameOpenBatchNFTChestCliked = async (chestInfos) => {
    const { success, message, data } = await openBatchNFTChest(chestInfos, () =>
      sendMsgToGameClient({ type: 'getConfirmOpenNFTChest', message: 'Confirm Successful' })
    );
    sendMsgToGameClient({ type: 'openNFTChestResult', message, success, hasWaiting: true, data });
  };

  const cocosDepositRodBtnClick = async (tokenIds) => {
    activeWaiting(true);
    const { success, message, data } = await depositRod(tokenIds);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'getNFTRodResult', message, success, hasWaiting: true, data });
  };

  const cocosWithdrawRodBtnClick = async (tokenIds) => {
    activeWaiting(true);
    const { success, message, data } = await withdrawRod(tokenIds);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'getNFTRodResult', message, success, hasWaiting: true, data });
  };

  const cocosRepairRodBtnClick = async (tokenIds, paymentToken) => {
    activeWaiting(true);
    const { success, message, data } = await repairRod(tokenIds, paymentToken);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'getNFTRodResult', message, success, hasWaiting: true, data });
    sendMsgToGameClient({ type: 'getRepairRodResult', message, success, hasWaiting: true, data });
  };

  const cocosDailyCheckInBtnClick = async (questId) => {
    activeWaiting(true);
    const { success, message, data } = await dailyCheckIn();
    activeWaiting(false);
    sendMsgToGameClient({ type: 'dailyCheckInResult', message, success, hasWaiting: true, data, questId });
  };

  const cocosWithdrawFishBtnClick = async (fishIds, tokenIds) => {
    activeWaiting(true);
    const { success, message, data } = await withdrawFish(fishIds, tokenIds);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'getNFTFishResult', message, success, hasWaiting: true, data });
  };

  const cocosMintFishBtnClick = async (fishIds) => {
    activeWaiting(true);
    const { success, message, data } = await mintFish(fishIds);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'getNFTFishResult', message, success, hasWaiting: true, data });
  };

  const cocosDepositFishBtnClick = async (tokenIds) => {
    activeWaiting(true);
    const { success, message, data } = await depositFish(tokenIds);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'getNFTFishResult', message, success, hasWaiting: true, data });
  };

  const cocosCookingBtnClick = async (stakedTokenIds, tokenIds) => {
    activeWaiting(true);
    const { success, message, transactionHash } = await cooking(stakedTokenIds, tokenIds);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'burnNFTFishResult', message, success, hasWaiting: true, transactionHash });
  };

  const onInGameCollectNFTFishBtnClicked = async (fish) => {
    activeWaiting(true);
    const { success, message, data } = await addToCollection(fish);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'collectNFTFishResult', message, success, hasWaiting: true, data });
  };

  const onInGameLinkWalletBtnClicked = async () => {
    const data = getWalletAvailability();
    sendMsgToGameClient({ type: 'walletProvidersAvailability', data });
  };

  const onWalletProviderSelected = async (providerKey) => {
    activeWaiting(true);
    const { success, message, data } = await linkWallet(providerKey);
    activeWaiting(false);
    sendMsgToGameClient({ type: 'linkWalletResult', message, success, hasWaiting: true, data });
  };

  const cocosLoadingProgress = async ({ progress, status }) => {
    if (progress === 100) setShowPlayBtn(true);
    setProgress(progress);
    setStatusText(status);
  };

  // manually detect video end
  const handleTimeUpdate = (e) => {
    if (e.target.currentTime >= e.target.duration - 0.5) {
      onIntroVideoFinish();
    }
  };

  const onIntroVideoFinish = async () => {
    if (progress === 100) setShowVideo(false);
  };

  const handleNotificationSettingsClick = async (data) => {
    if (data.isTurnOn) {
      enableGameNotifications();
    } else {
      disableNotifications();
    }
  };

  return (
    <div style={containerStyle}>
      {showVideo && !isVideoLoaded && <img src={gameImage} alt="game" className="intro-image" />}
      {showVideo && (
        <div className="intro-container">
          <video
            className="intro-video"
            autoPlay
            muted
            loop
            playsInline
            onCanPlay={() => setIsVideoLoaded(true)}
            onTimeUpdate={handleTimeUpdate}
            onError={() => setIsVideoLoaded(false)}
            controls={false}
            preload="auto">
            <source src={videoURL} type="video/mp4" />
          </video>
          <div className="loading-waiting-container">
            <span className="loading-resource-text">{statusText}</span>
            <div className="loading-waiting-text">
              <div className="progress-bar-container">
                <div
                  className={`progress-bar-text ${
                    showPlayBtn ? 'progress-bar-text-complete' : 'progress-bar-text-loading'
                  }`}></div>
                <div
                  className="progress-bar"
                  style={{
                    width: `${progress}%`,
                    marginLeft: progress === 100 ? '0' : undefined,
                  }}></div>
              </div>
              {showPlayBtn ? (
                <button className="Play-button" onClick={() => setShowVideo(false)}>
                  <span className="press-start-2p-regular">Play Now</span>
                </button>
              ) : (
                <div className="loading-waiting-message">
                  <div>First load may take longer</div>
                  <div>Thank you for your patience</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <iframe
        ref={iframeRef}
        src="/cocos/index.html"
        title="Cocos Game"
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: showVideo ? -1 : 1,
          visibility: showVideo ? 'hidden' : 'visible',
        }}
      />

      {isShowBtn && (
        <>
          <button onClick={toggleDevMode} className="First-button">
            {devMode ? 'Exit Dev Mode' : 'Dev Mode'}
          </button>

          {devMode && (
            <>
              <input
                className="Second-button"
                type="text"
                placeholder="Test Enter token IDs, comma-separated"
                onChange={handleTokenIdsChange}
              />
              {/* <button className="Third-button" onClick={() => cocosMintFishBtnClick(['67eb666a2f3ef82ec2f25a53'])}>
                Test: Mint Fish
              </button> */}
              {/* <button className="Third-button" onClick={() => onInGameCollectNFTFishBtnClicked([4])}>
                Test: Add Collection
              </button> */}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default GameComponent;
